import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Switch,
  FormGroup,
  Divider,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Star,
  StarBorder,
  Layers,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import subscriptionService, { Plan, Feature, getAvailableFeatures, AVAILABLE_FEATURES } from '../../services/subscriptionService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';

interface PlanFormData {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyDiscount: number;
  features: string[];
  isPopular: boolean;
  sortOrder: number;
}

const initialFormData: PlanFormData = {
  name: '',
  description: '',
  monthlyPrice: 0,
  yearlyPrice: 0,
  yearlyDiscount: 0,
  features: [],
  isPopular: false,
  sortOrder: 0,
};

const PlansPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>(AVAILABLE_FEATURES); // Start with fallback
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);

  // Fetch plans and features
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, featuresData] = await Promise.all([
        subscriptionService.getAllPlans(),
        getAvailableFeatures(),
      ]);
      setPlans(plansData);
      setFeatures(featuresData);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load plans', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Filter plans
  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handlers
  const handleOpenCreate = () => {
    setEditingPlan(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      monthlyPrice: plan.monthlyPrice,
      yearlyPrice: plan.yearlyPrice,
      yearlyDiscount: plan.yearlyDiscount,
      features: plan.features,
      isPopular: plan.isPopular,
      sortOrder: plan.sortOrder,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
    setFormData(initialFormData);
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSelectAllFeatures = () => {
    if (formData.features.length === features.length) {
      setFormData(prev => ({ ...prev, features: [] }));
    } else {
      setFormData(prev => ({ ...prev, features: features.map(f => f.key) }));
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Plan name is required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (editingPlan) {
        await subscriptionService.updatePlan(editingPlan._id, formData);
        enqueueSnackbar('Plan updated successfully', { variant: 'success' });
      } else {
        await subscriptionService.createPlan(formData);
        enqueueSnackbar('Plan created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchPlans();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to save plan', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (plan: Plan) => {
    setDeletingPlan(plan);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;

    try {
      await subscriptionService.deletePlan(deletingPlan._id);
      enqueueSnackbar('Plan deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingPlan(null);
      fetchPlans();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to delete plan', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (plan: Plan) => {
    try {
      await subscriptionService.updatePlan(plan._id, { isActive: !plan.isActive });
      enqueueSnackbar(`Plan ${plan.isActive ? 'deactivated' : 'activated'}`, { variant: 'success' });
      fetchPlans();
    } catch (error: any) {
      enqueueSnackbar('Failed to update plan status', { variant: 'error' });
    }
  };

  // Loading skeleton
  if (loading && plans.length === 0) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Subscription Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage subscription plans for schools
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ borderRadius: 2 }}
        >
          Create Plan
        </Button>
      </Box>

      {/* Search */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search plans..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Paper>

      {/* Plans Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Plan Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Monthly</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Yearly</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Features</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <Layers sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No plans match your search' : 'No plans created yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPlans.map((plan) => (
                  <TableRow key={plan._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {plan.isPopular && (
                          <Tooltip title="Popular Plan">
                            <Star sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                          </Tooltip>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {plan.name}
                          </Typography>
                          {plan.description && (
                            <Typography variant="caption" color="text.secondary">
                              {plan.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        PKR {plan.monthlyPrice.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">/month</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        PKR {plan.yearlyPrice.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /year {plan.yearlyDiscount > 0 && `(${plan.yearlyDiscount}% off)`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {plan.features.slice(0, 3).map((feature) => (
                          <Chip
                            key={feature}
                            label={features.find(f => f.key === feature)?.label || feature}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))}
                        {plan.features.length > 3 && (
                          <Chip
                            label={`+${plan.features.length - 3}`}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: alpha(plan.isActive ? theme.palette.success.main : theme.palette.grey[500], 0.1),
                          color: plan.isActive ? theme.palette.success.main : theme.palette.grey[500],
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(plan)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={plan.isActive ? 'Deactivate' : 'Activate'}>
                          <Switch
                            size="small"
                            checked={plan.isActive}
                            onChange={() => handleToggleStatus(plan)}
                          />
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleOpenDelete(plan)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPlan ? 'Edit Plan' : 'Create Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sortOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Monthly Price (PKR)"
                value={formData.monthlyPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, monthlyPrice: parseFloat(e.target.value) || 0 }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Yearly Price (PKR)"
                value={formData.yearlyPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, yearlyPrice: parseFloat(e.target.value) || 0 }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Yearly Discount (%)"
                value={formData.yearlyDiscount}
                onChange={(e) => setFormData(prev => ({ ...prev, yearlyDiscount: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPopular}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {formData.isPopular ? <Star sx={{ color: theme.palette.warning.main }} /> : <StarBorder />}
                    Mark as Popular (shown with badge)
                  </Box>
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Features Included
                </Typography>
                <Button size="small" onClick={handleSelectAllFeatures}>
                  {formData.features.length === features.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              <FormGroup row>
                {features.map((feature) => (
                  <FormControlLabel
                    key={feature.key}
                    control={
                      <Checkbox
                        checked={formData.features.includes(feature.key)}
                        onChange={() => handleFeatureToggle(feature.key)}
                        size="small"
                      />
                    }
                    label={feature.label}
                    sx={{ width: '48%', mr: 0 }}
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Plan?"
        message={`Are you sure you want to delete "${deletingPlan?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default PlansPage;
